import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Edit3, Check, X } from "lucide-react";

export default function Properties() {
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");

  const isSuperAdmin = user?.role === "super_admin" || user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/properties");
      setProps(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/properties", { name: newName.trim(), address: newAddress.trim() || null });
      toast.success("Property created");
      setNewName("");
      setNewAddress("");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete "${name}" and all its data? This cannot be undone!`)) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success(`"${name}" deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6 sm:p-10">
        <p className="text-zinc-500">You don't have access to this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow mb-1">System</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
            Properties
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage all properties and their access.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="signage-btn-primary h-10 px-4 flex items-center gap-2 text-sm"
        >
          <Plus className="h-4 w-4" />
          Add Property
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="signage-card p-5 mb-6 max-w-lg">
          <h3 className="font-display font-bold text-lg mb-4">New Property</h3>
          <div className="space-y-4">
            <div>
              <Label>Property Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="mt-1 rounded-none h-10"
                placeholder="e.g. Hotel Grand"
                required
              />
            </div>
            <div>
              <Label>Address (optional)</Label>
              <Input
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                className="mt-1 rounded-none h-10"
                placeholder="e.g. Jakarta"
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={busy} className="signage-btn-primary h-9 px-4 text-sm">
                {busy ? "Creating…" : "Create"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="signage-btn-ghost h-9 px-4 text-sm">
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {props.length === 0 && (
          <p className="text-zinc-500 text-sm">No properties yet.</p>
        )}
        {props.map((p) => {
          const isEditing = editingId === p.id;
          return (
            <div key={p.id} className="signage-card p-5 flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="h-10 w-10 bg-[#002FA7]/10 flex items-center justify-center rounded shrink-0">
                  <Building2 className="h-5 w-5 text-[#002FA7]" />
                </div>
                {isEditing ? (
                  <div className="flex-1 space-y-2">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="rounded-none h-9 text-sm"
                      placeholder="Property name"
                      required
                    />
                    <Input
                      value={editAddress}
                      onChange={(e) => setEditAddress(e.target.value)}
                      className="rounded-none h-9 text-sm"
                      placeholder="Address"
                    />
                  </div>
                ) : (
                  <div className="min-w-0">
                    <p className="font-medium truncate">{p.name}</p>
                    {p.address && <p className="text-xs text-zinc-500 truncate">{p.address}</p>}
                    <p className="text-[10px] text-zinc-400 font-mono">ID: {p.id.slice(0, 8)}…</p>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-3">
                {isEditing ? (
                  <>
                    <button
                      onClick={async () => {
                        if (!editName.trim()) return;
                        setBusy(true);
                        try {
                          await api.put(`/properties/${p.id}`, {
                            name: editName.trim(),
                            address: editAddress.trim() || null
                          });
                          toast.success("Property updated");
                          setEditingId(null);
                          load();
                        } catch (err) {
                          toast.error(formatApiError(err));
                        } finally {
                          setBusy(false);
                        }
                      }}
                      disabled={busy}
                      className="p-2 hover:bg-green-50 hover:text-green-600 rounded"
                      title="Save"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="p-2 hover:bg-zinc-100 rounded"
                      title="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setEditingId(p.id);
                        setEditName(p.name);
                        setEditAddress(p.address || "");
                      }}
                      className="p-2 hover:bg-blue-50 hover:text-blue-600 rounded"
                      title="Edit property"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => remove(p.id, p.name)}
                      className="p-2 hover:bg-red-50 hover:text-red-600 rounded"
                      title="Delete property"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
